import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Badge,
  Stack,
  Button,
  Grid,
  TextInput,
  Select,
  Pagination,
  Loader,
  Center,
  Alert,
} from "@mantine/core";
import { IconSearch, IconDownload, IconCalendar, IconCheck, IconClock } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { publicTenderApi, PublicTender } from "../../../api/publicTenders";
import { useTenderRegistrationStatus, useRegisterForTender } from "../hooks/useTenderRegistrations";

interface Tender extends PublicTender {
  requireRegistration: boolean;
  requirePrebidMeeting: boolean;
}

export function TenderBrowse() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [downloading, setDownloading] = useState<string | null>(null);

  const { data: tenders, isLoading, error } = useQuery({
    queryKey: ["public-tenders", searchTerm, selectedCategory, currentPage],
    queryFn: async () => {
      const result = await publicTenderApi.getPublishedTenders({
        search: searchTerm || undefined,
        category: selectedCategory || undefined,
        page: currentPage,
        limit: 10,
      });
      return result;
    },
  });

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (value: string | null) => {
    setSelectedCategory(value || "");
    setCurrentPage(1);
  };

  const handleDownloadTor = async (tenderId: string) => {
    setDownloading(tenderId);
    try {
      await publicTenderApi.downloadTorDocument(tenderId);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setDownloading(null);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "GOODS":
        return "blue";
      case "SERVICES":
        return "green";
      case "WORKS":
        return "orange";
      default:
        return "gray";
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case "PUBLISHED":
        return "blue";
      case "COLLECTION":
        return "cyan";
      case "OPENED":
        return "green";
      default:
        return "gray";
    }
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>Browse Tenders</Title>
          <Text c="dimmed" mt="xs">
            View and register for available tenders
          </Text>
        </div>

        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <TextInput
              placeholder="Search tenders..."
              leftSection={<IconSearch size={16} />}
              value={searchTerm}
              onChange={(e) => handleSearch(e.currentTarget.value)}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Select
              placeholder="Filter by category"
              data={[
                { value: "", label: "All Categories" },
                { value: "GOODS", label: "Goods" },
                { value: "SERVICES", label: "Services" },
                { value: "WORKS", label: "Works" },
              ]}
              value={selectedCategory}
              onChange={handleCategoryChange}
            />
          </Grid.Col>
        </Grid>

        {isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : error ? (
          <Alert color="red" title="Error">
            Failed to load tenders. Please try again.
          </Alert>
        ) : !tenders?.data || tenders.data.length === 0 ? (
          <Alert color="green" title="No Tenders Found">
            No tenders match your search criteria.
          </Alert>
        ) : (
          <>
            <Grid>
              {tenders.data.map((tender) => (
                <Grid.Col key={tender.id} span={12}>
                  <TenderCard
                    tender={{
                      ...tender,
                      requireRegistration: tender.requireRegistration || false,
                      requirePrebidMeeting: tender.requirePrebidMeeting || false,
                    } as Tender}
                    onView={() => navigate(`/vendor/tenders/${tender.id}`)}
                    onDownloadTor={() => handleDownloadTor(tender.id)}
                    downloading={downloading === tender.id}
                    getCategoryColor={getCategoryColor}
                    getStageColor={getStageColor}
                  />
                </Grid.Col>
              ))}
            </Grid>

            {tenders.meta && tenders.meta.totalPages > 1 && (
              <Pagination
                total={tenders.meta.totalPages}
                value={currentPage}
                onChange={setCurrentPage}
                mt="md"
              />
            )}
          </>
        )}
      </Stack>
    </Container>
  );
}

function TenderCard({
  tender,
  onView,
  onDownloadTor,
  downloading,
  getCategoryColor,
  getStageColor,
}: {
  tender: Tender;
  onView: () => void;
  onDownloadTor: () => void;
  downloading: boolean;
  getCategoryColor: (category: string) => string;
  getStageColor: (stage: string) => string;
}) {
  const { data: registrationStatus } = useTenderRegistrationStatus(tender.id);
  const registerForTender = useRegisterForTender();

  const handleRegister = async () => {
    try {
      await registerForTender.mutateAsync(tender.id);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const isRegistered = registrationStatus?.registration?.status === 'REGISTERED';
  const deadlinePassed = dayjs(tender.submissionDeadline).isBefore(dayjs());

  return (
    <Card shadow="sm" padding="md" radius="md" withBorder>
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" gap="sm">
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text fw={500} size="md" lineClamp={2}>
              {tender.description}
            </Text>
            <Group gap="xs" mt={4}>
              <Text size="xs" c="dimmed">
                {tender.referenceNo}
              </Text>
              <Badge color={getCategoryColor(tender.category)} size="sm">{tender.category}</Badge>
              <Badge color={getStageColor(tender.stage)} size="sm">
                {tender.stage}
              </Badge>
              {tender.requireRegistration && (
                <Badge color="orange" size="sm">
                  Registration Required
                </Badge>
              )}
              {isRegistered && (
                <Badge color="green" size="sm" leftSection={<IconCheck size={10} />}>
                  Registered
                </Badge>
              )}
            </Group>
          </div>
        </Group>

        <Group gap="md" c="dimmed" wrap="nowrap">
          <Group gap={4}>
            <IconCalendar size={12} />
            <Text size="xs">Publish: {dayjs(tender.publishingDate).format("MMM DD, YYYY")}</Text>
          </Group>
          <Group gap={4}>
            <IconClock size={12} />
            <Text size="xs" c={deadlinePassed ? "red" : "dimmed"}>
              Deadline: {dayjs(tender.submissionDeadline).format("MMM DD, YYYY")}
            </Text>
          </Group>
          {tender.requirePrebidMeeting && tender.prebidMeetingDate && (
            <Text size="xs" c="green">
              Pre-bid: {dayjs(tender.prebidMeetingDate).format("MMM DD, YYYY")}
            </Text>
          )}
        </Group>

        <Group justify="space-between" gap="xs" mt={4}>
          <Group gap="xs">
            {tender.torDocument && (
              <Button
                size="xs"
                variant="light"
                leftSection={<IconDownload size={12} />}
                onClick={onDownloadTor}
                loading={downloading}
              >
                TOR
              </Button>
            )}
          </Group>
          <Group gap="xs">
            {tender.requireRegistration && !isRegistered && !deadlinePassed && (
              <Button 
                size="xs" 
                variant="filled" 
                color="green" 
                onClick={handleRegister} 
                loading={registerForTender.isPending}
              >
                Register
              </Button>
            )}
            <Button size="xs" variant="light" color="green" onClick={onView}>
              View Details
            </Button>
          </Group>
        </Group>
      </Stack>
    </Card>
  );
}

